import { Box, IconButton, styled } from "@mui/material";
import { ChevronLeft, ChevronRight, Folder } from "@procore/core-icons";
import { Search, Typography } from "@procore/core-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { PROCORE_TOP_BAR } from "components/toolbars/constants";

import { selectMiniMapDrawings, selectMiniMapLevels, selectTempDrawingId, setTempDrawingId } from "./minimapSlice";
import { formatLevelDisplayName } from "./miniMapUtils";
import type { Level } from "./types";

const DEFAULT_DRAWER_TOP_OFFSET = PROCORE_TOP_BAR.HEIGHT + 24;
const DRAWER_COLLAPSED_HEIGHT = 72;
const SWIPE_THRESHOLD = 30;

const DrawerContainer = styled(Box, {
    shouldForwardProp: (prop) =>
        prop !== "open" && prop !== "collapsed" && prop !== "fullWidth" && prop !== "topOffset",
})<{ open: boolean; collapsed: boolean; fullWidth: boolean; topOffset: number }>(
    ({ open, collapsed, fullWidth, topOffset }) => ({
        position: "absolute",
        top: collapsed ? `calc(100% - ${DRAWER_COLLAPSED_HEIGHT}px)` : topOffset,
        left: fullWidth ? 0 : 24,
        right: fullWidth ? 0 : "auto",
        bottom: 0,
        width: fullWidth ? "100%" : "100%",
        maxWidth: fullWidth ? "100%" : 377,
        backgroundColor: "#FFFFFF",
        borderRadius: "8px 8px 0 0",
        boxShadow: fullWidth ? "0px -2px 12px 0px rgba(0, 0, 0, 0.2)" : "4px 0px 12px 0px rgba(0, 0, 0, 0.2)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        zIndex: 10,
        transform: open ? "translateX(0)" : "translateX(-120%)",
        transition: "transform 300ms ease-out, top 300ms ease-out",
        pointerEvents: open ? "auto" : "none",
    }),
);

const DragArea = styled(Box)({
    cursor: "grab",
    touchAction: "none",
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    userSelect: "none",
    "&:active": {
        cursor: "grabbing",
    },
});

const DragHandle = styled(Box)({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "8px 0",
});

const DragBar = styled(Box)({
    width: 32,
    height: 4,
    backgroundColor: "#C4C4C4",
    borderRadius: 2,
});

const DrawerHeader = styled(Box)({
    display: "flex",
    alignItems: "center",
    padding: "4px 16px 12px 16px",
    minHeight: 44,
});

const BackButton = styled(IconButton)({
    padding: 4,
    marginRight: 8,
    marginLeft: -4,
    "& svg": {
        width: 20,
        height: 20,
        color: "#6A767C",
    },
});

const SearchContainer = styled(Box)({
    padding: "12px 16px",
    boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.1)",
});

const ContentWrapper = styled(Box)({
    flex: 1,
    position: "relative",
    overflow: "hidden",
});

const SlideContainer = styled(Box)<{ direction: "left" | "right" | "none" }>(({ direction }) => ({
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: "auto",
    WebkitOverflowScrolling: "touch",
    animation: direction !== "none" ? `slide-${direction} 250ms ease-out` : "none",
    "@keyframes slide-left": {
        from: { transform: "translateX(100%)" },
        to: { transform: "translateX(0)" },
    },
    "@keyframes slide-right": {
        from: { transform: "translateX(-100%)" },
        to: { transform: "translateX(0)" },
    },
}));

const ListRow = styled(Box)<{ selected?: boolean }>(({ selected }) => ({
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    cursor: "pointer",
    backgroundColor: selected ? "rgba(32, 102, 223, 0.08)" : "transparent",
    "&:active": {
        backgroundColor: selected ? "rgba(32, 102, 223, 0.12)" : "rgba(0, 0, 0, 0.04)",
    },
}));

const IconContainer = styled(Box)({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    marginRight: 12,
    flexShrink: 0,
});

const ChevronContainer = styled(Box)({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    marginLeft: "auto",
    flexShrink: 0,
    "& svg": {
        width: 16,
        height: 16,
        color: "#6A767C",
    },
});

const RowLabel = styled("span")({
    flex: 1,
    fontSize: 14,
    color: "#232729",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
});

const DrawingIcon = () => (
    <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0 0V18H20V7H15V0H0ZM8.5 4.5V2.5H12.5V9.5H17.5V15.5H8.5V12H6V15.5H2.5V9.5H8.5V7H2.5V2.5H6V4.5H8.5Z"
            fill="#6A767C"
        />
    </svg>
);

const LevelHeader = styled(Box)({
    padding: "12px 16px 8px 16px",
    fontSize: 14,
    fontWeight: 600,
    color: "#232729",
});

interface LevelsDrawerMobileProps {
    open: boolean;
    largeMapOpen: boolean;
    onInsideLevelChange?: (isInsideLevel: boolean) => void;
    fullWidth?: boolean;
    topOffset?: number;
}

export function LevelsDrawerMobile({
    open,
    largeMapOpen,
    onInsideLevelChange,
    fullWidth = false,
    topOffset = DEFAULT_DRAWER_TOP_OFFSET,
}: LevelsDrawerMobileProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const levels = useAppSelector(selectMiniMapLevels);
    const drawings = useAppSelector(selectMiniMapDrawings);
    const tempDrawingId = useAppSelector(selectTempDrawingId);

    const [currentLevel, setCurrentLevel] = useState<Level | null>(null);
    const [slideDirection, setSlideDirection] = useState<"left" | "right" | "none">("none");
    const [searchTerm, setSearchTerm] = useState("");
    const [collapsed, setCollapsed] = useState(true);

    const hasAutoNavigatedRef = useRef(false);

    useEffect(() => {
        if (!largeMapOpen) {
            setCurrentLevel(null);
            setCollapsed(true);
            setSearchTerm("");
            hasAutoNavigatedRef.current = false;
        } else {
            setCollapsed(true);
        }
    }, [largeMapOpen]);

    useEffect(() => {
        if (!largeMapOpen || hasAutoNavigatedRef.current || !tempDrawingId) {
            return;
        }
        const levelWithDrawing = levels.find((level) => level.drawingIds.includes(tempDrawingId));
        if (levelWithDrawing) {
            setCurrentLevel(levelWithDrawing);
            setSlideDirection("none");
            hasAutoNavigatedRef.current = true;
        }
    }, [largeMapOpen, tempDrawingId, levels]);

    useEffect(() => {
        onInsideLevelChange?.(currentLevel !== null);
    }, [currentLevel, onInsideLevelChange]);

    // Drag handling for collapse/expand
    const dragStartYRef = useRef<number | null>(null);
    const isDraggingRef = useRef(false);

    const handleDragStart = useCallback((clientY: number) => {
        dragStartYRef.current = clientY;
        isDraggingRef.current = true;
    }, []);

    const handleDragEnd = useCallback(
        (clientY: number) => {
            if (!isDraggingRef.current || dragStartYRef.current === null) {
                return;
            }

            const deltaY = clientY - dragStartYRef.current;

            if (collapsed) {
                if (deltaY < -SWIPE_THRESHOLD) {
                    setCollapsed(false);
                }
            } else {
                if (deltaY > SWIPE_THRESHOLD) {
                    setCollapsed(true);
                }
            }

            dragStartYRef.current = null;
            isDraggingRef.current = false;
        },
        [collapsed],
    );

    const handlePointerDown = useCallback(
        (e: React.PointerEvent) => {
            e.preventDefault();
            handleDragStart(e.clientY);
        },
        [handleDragStart],
    );

    const handlePointerUp = useCallback(
        (e: React.PointerEvent) => {
            handleDragEnd(e.clientY);
        },
        [handleDragEnd],
    );

    const handlePointerCancel = useCallback(() => {
        dragStartYRef.current = null;
        isDraggingRef.current = false;
    }, []);

    const handleLevelClick = useCallback((level: Level) => {
        setSlideDirection("left");
        setCurrentLevel(level);
    }, []);

    const handleBackClick = useCallback(() => {
        setSlideDirection("right");
        setCurrentLevel(null);
    }, []);

    const handleDrawingClick = useCallback(
        (drawingId: string) => {
            if (drawingId !== tempDrawingId) {
                dispatch(setTempDrawingId(drawingId));
            }
            setCollapsed(true);
        },
        [dispatch, tempDrawingId],
    );

    const isSearching = searchTerm.trim().length > 0;

    const searchResults = useMemo(() => {
        if (!isSearching || currentLevel) {
            return [];
        }

        const lowerSearch = searchTerm.toLowerCase();
        const results: { level: Level; matchingDrawingIds: string[] }[] = [];

        levels.forEach((level) => {
            const { displayName } = formatLevelDisplayName(level.name);
            const levelNameMatches = displayName.toLowerCase().includes(lowerSearch);

            const matchingDrawingIds = level.drawingIds.filter((drawingId) => {
                const drawing = drawings[drawingId];
                return drawing?.name.toLowerCase().includes(lowerSearch);
            });

            if (levelNameMatches || matchingDrawingIds.length > 0) {
                results.push({
                    level,
                    matchingDrawingIds: levelNameMatches ? level.drawingIds : matchingDrawingIds,
                });
            }
        });

        return results;
    }, [isSearching, currentLevel, searchTerm, levels, drawings]);

    const filteredLevels = useMemo(() => {
        if (isSearching) {
            return []; // Don't show levels list when searching at root level
        }
        return levels;
    }, [levels, isSearching]);

    const filteredDrawingIds = useMemo(() => {
        if (!currentLevel) {
            return [];
        }

        if (!searchTerm.trim()) {
            return currentLevel.drawingIds;
        }

        const lowerSearch = searchTerm.toLowerCase();
        return currentLevel.drawingIds.filter((drawingId) => {
            const drawing = drawings[drawingId];
            return drawing?.name.toLowerCase().includes(lowerSearch);
        });
    }, [currentLevel, drawings, searchTerm]);

    const headerTitle = useMemo(() => {
        if (currentLevel) {
            if (tempDrawingId && currentLevel.drawingIds.includes(tempDrawingId)) {
                const selectedDrawing = drawings[tempDrawingId];
                if (selectedDrawing) {
                    return selectedDrawing.name;
                }
            }
            const { displayName } = formatLevelDisplayName(currentLevel.name);
            return displayName;
        }
        return t("levelDrawerHeader");
    }, [currentLevel, tempDrawingId, drawings, t]);

    const renderDrawingRow = (drawingId: string) => {
        const drawing = drawings[drawingId];
        if (!drawing) {
            return null;
        }
        const isSelected = drawingId === tempDrawingId;
        return (
            <ListRow key={drawingId} selected={isSelected} onClick={() => handleDrawingClick(drawingId)}>
                <IconContainer>
                    <DrawingIcon />
                </IconContainer>
                <RowLabel>{drawing.name}</RowLabel>
            </ListRow>
        );
    };

    const renderContent = () => {
        if (currentLevel) {
            return filteredDrawingIds.map(renderDrawingRow);
        }

        if (isSearching) {
            return searchResults.map(({ level, matchingDrawingIds }) => {
                const { displayName } = formatLevelDisplayName(level.name);
                return (
                    <Box key={level.id}>
                        <LevelHeader>{displayName}</LevelHeader>
                        {matchingDrawingIds.map(renderDrawingRow)}
                    </Box>
                );
            });
        }

        return filteredLevels.map((level) => {
            const { displayName } = formatLevelDisplayName(level.name);
            return (
                <ListRow key={level.id} onClick={() => handleLevelClick(level)}>
                    <IconContainer>
                        <Folder style={{ width: 20, height: 20, color: "#6A767C" }} />
                    </IconContainer>
                    <RowLabel>{displayName}</RowLabel>
                    <ChevronContainer>
                        <ChevronRight />
                    </ChevronContainer>
                </ListRow>
            );
        });
    };

    return (
        <DrawerContainer open={open} collapsed={collapsed} fullWidth={fullWidth} topOffset={topOffset}>
            <DragArea
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}
            >
                <DragHandle>
                    <DragBar />
                </DragHandle>
                <DrawerHeader>
                    {currentLevel && (
                        <BackButton onClick={handleBackClick}>
                            <ChevronLeft />
                        </BackButton>
                    )}
                    <Typography
                        intent="h3"
                        style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            color: "#232729",
                        }}
                    >
                        {headerTitle}
                    </Typography>
                </DrawerHeader>
            </DragArea>
            {!collapsed && (
                <>
                    <SearchContainer>
                        <Search
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </SearchContainer>
                    <ContentWrapper>
                        <SlideContainer key={currentLevel?.id ?? "root"} direction={slideDirection}>
                            {renderContent()}
                        </SlideContainer>
                    </ContentWrapper>
                </>
            )}
        </DrawerContainer>
    );
}
